import { useEffect, useRef } from 'react';

/**
 * Wire-format of every event delivered on the `/v1/me/events` SSE channel.
 * MUST stay in sync with `server/src/integration/chat/user-events.service.ts`
 * (`UserEvent` union). Adding a new variant?
 *   1. Add it here AND on the server.
 *   2. Either dispatch it from a controller (`publish` for per-user,
 *      `broadcast` for tenant-wide) AND consume it via `useServerEvent`.
 *   3. Wire your handler into a TanStack Query invalidation OR a custom
 *      effect — the bus itself doesn't care.
 */
export type ServerEvent =
  | {
      type: 'conversation.activity';
      conversationId: string;
      updatedAt?: string;
      /**
       * The assistant message id of the turn that fired this activity (global-workspaces phase 4
       * fan-out). Present on chat-runner completions so an audience member watching the shared thread
       * can reattach to that message's stream; absent on other activity sources.
       */
      messageId?: string;
      status?: 'active' | 'archived';
      archivedAt?: string | null;
    }
  | {
      type: 'conversation.archived';
      version: 1;
      conversationId: string;
      archivedAt: string;
    }
  | {
      type: 'conversation.restored';
      version: 1;
      conversationId: string;
      archivedAt: null;
    }
  | { type: 'prefs.changed' }
  | { type: 'connector.changed' }
  | { type: 'maintenance.changed' }
  | { type: 'access-request.changed' }
  | { type: 'release-note.published' }
  /**
   * A smart skill suggestion was raised for a finished turn. Pushed on the user feed rather than the
   * message stream because the judge runs after `done`, when that stream is usually already closed.
   */
  | {
      type: 'skill.suggestion';
      conversationId: string;
      messageId: string;
      suggestionId: string;
    }
  /**
   * A change to workspace memory was proposed off the back of a finished turn. Pushed on the user feed
   * for the same reason as the skill offer: the extraction judge answers after `done`, when the
   * message stream is usually already closed.
   *
   * camelCase, unlike the REST wire — every event on this channel is (canon §15.4).
   */
  | {
      type: 'memory.proposal';
      conversationId: string;
      messageId: string;
      proposalId: string;
      scope: 'shared' | 'personal';
    };

export type ServerEventType = ServerEvent['type'];

/**
 * Process-local pub/sub for SSE events parsed from `/v1/me/events`. We use
 * a `DOM EventTarget` rather than node's EventEmitter so it works in any
 * runtime (browser, Vitest, RSC) without polyfills.
 *
 * Single instance per module load — React Fast Refresh re-runs the module
 * only when the file itself changes, so listeners installed via hooks
 * survive normal component re-mounts cleanly.
 */
const bus = new EventTarget();

/** Stream parser fan-out — called from `useUserEventStream` on each event. */
export function dispatchServerEvent(event: ServerEvent): void {
  bus.dispatchEvent(new CustomEvent(event.type, { detail: event }));
}

/**
 * Subscribe a component to a single SSE event type. Re-installs the
 * listener if `type` or `handler` changes; otherwise idempotent.
 *
 * Example:
 *   useServerEvent('connector.changed', () => {
 *     qc.invalidateQueries({ queryKey: ['connectors-catalog'] });
 *   });
 */
export function useServerEvent<T extends ServerEventType>(
  type: T,
  handler: (event: Extract<ServerEvent, { type: T }>) => void,
): void {
  // Stable ref so callers can pass inline arrows without re-subscribing on
  // every render. The DOM addEventListener identity-comparison would tear
  // down + reinstall the listener otherwise, which races against in-flight
  // events.
  const handlerRef = useRef(handler);
  useEffect(() => {
    handlerRef.current = handler;
  }, [handler]);

  useEffect(() => {
    const listener = (ev: Event): void => {
      const detail = (ev as CustomEvent<Extract<ServerEvent, { type: T }>>)
        .detail;
      handlerRef.current(detail);
    };
    bus.addEventListener(type, listener);
    return () => bus.removeEventListener(type, listener);
  }, [type]);
}
